import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TablePagination,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { getApiUrl } from "../../api-clients";
import {
  ExpenseApi,
  ExpenseDto,
  ExpenseDtoAmountPaginatedDto,
  ExpenseFilterDto,
  FurnisherApi,
  FurnisherDto,
  FurnisherExpenseFilterDto,
  FurnisherExpenseStatisticDto,
  SortOrderType,
  StatisticApi,
} from "../../api-clients/api";
import Loading from "../Shared/Loading";
import NoResult from "../Shared/NoResult";
import CRUDFurnisher from "../CRUD/CRUDFurnisher";
import { Panel } from "@fluentui/react";
import LabelFieldValue from "../Shared/LabelFieldValue";
import FormButtons from "../Shared/FormButtons";
import FluentUiDatePicker from "../Shared/FluentUiDatePicker";
import { dateToString, numberToMad } from "../../helper";
import { useTranslation } from "react-i18next";
import ClearIcon from "@material-ui/icons/Clear";
import CheckIcon from "@material-ui/icons/Check";
import MoneyOffIcon from "@material-ui/icons/MoneyOff";
import { useRole } from "../../hooks/useRole";

export default function Furnisher() {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const { furnisherId }: any = useParams();
  const history = useHistory();
  const [furnisherApi] = useState<FurnisherApi>(new FurnisherApi(getApiUrl()));
  const [statisticApi] = useState<StatisticApi>(new StatisticApi(getApiUrl()));
  const [expenseApi] = useState<ExpenseApi>(new ExpenseApi(getApiUrl()));
  const [furnisher, setFurnisher] = useState<FurnisherDto | null>(null);
  const [furnisherToEdit, setFurnisherToEdit] = useState<FurnisherDto | null>(
    null
  );
  const [furnisherLoaded, setFurnisherLoaded] = useState<boolean>(false);
  const [checkDelete, setCheckDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState<Date>(
    new Date(currentYear, 0, 1)
  );
  const [endDate, setEndDate] = useState<Date>(new Date(currentYear, 11, 31));
  const [statistic, setStatistic] =
    useState<FurnisherExpenseStatisticDto | null>(null);
  const [statisticLoading, setStatisticLoading] = useState<boolean>(true);
  const expensesPageSize = 7;
  const [expensesPageIndex, setExpensesPageIndex] = useState<number>(0);
  const [expensesResult, setExpensesResult] =
    useState<ExpenseDtoAmountPaginatedDto | null>(null);
  const [expensesLoading, setExpensesLoading] = useState<boolean>(true);

  useEffect(() => {
    if (furnisher == null) {
      setFurnisherLoaded(false);
      furnisherApi
        .getById(furnisherId)
        .then((res) => {
          setFurnisher(res);
          setFurnisherLoaded(true);
        })
        .catch((err) => {
          console.log(err);
          setFurnisherLoaded(true);
        });
    }
  }, [furnisherApi, furnisherId, furnisher]);

  useEffect(() => {
    setStatisticLoading(true);
    statisticApi
      .furnisherExpenses({
        furnisherId: furnisherId,
        startDate: startDate,
        endDate: endDate,
      } as FurnisherExpenseFilterDto)
      .then((res) => {
        setStatistic(res);
        setStatisticLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setStatisticLoading(false);
      });
  }, [statisticApi, furnisherId, startDate, endDate]);

  useEffect(() => {
    setExpensesPageIndex(0);
  }, [startDate, endDate]);

  useEffect(() => {
    setExpensesLoading(true);
    expenseApi
      .filter({
        furnishers: [furnisherId],
        startDate: startDate,
        endDate: endDate,
        pageIndex: expensesPageIndex,
        pageSize: expensesPageSize,
        sortField: "Date",
        sortOrder: SortOrderType._1,
      } as ExpenseFilterDto)
      .then((res) => {
        setExpensesResult(res);
        setExpensesLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setExpensesLoading(false);
      });
  }, [expenseApi, furnisherId, startDate, endDate, expensesPageIndex]);

  const deleteFurnisher = () => {
    setDeleting(true);
    furnisherApi
      .deleteById(furnisherId)
      .then(() => {
        setDeleting(false);
        history.push("/furnishers");
      })
      .catch((err: any) => {
        setDeleting(false);
        console.log(err);
      });
  };

  return (
    <Container maxWidth="lg">
      {furnisherLoaded ? (
        furnisher != null ? (
          <>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h5">{t("furnishers.singular")}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <LabelFieldValue
                      label={t("furnishers.name")}
                      value={furnisher.name}
                    />
                  </CardContent>
                  <CardActions disableSpacing>
                    {!isAdmin ? null : checkDelete ? (
                      <Grid container alignItems="center">
                        <Grid item xs={2}>
                          <Typography variant="subtitle1">
                            {t("common.confirmDelete")}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            aria-label={t("common.cancel")}
                            disabled={deleting}
                            onClick={() => setCheckDelete(false)}
                            color="inherit"
                          >
                            <ClearIcon />
                          </IconButton>
                          <IconButton
                            aria-label={t("common.confirm")}
                            disabled={deleting}
                            onClick={deleteFurnisher}
                            color="secondary"
                          >
                            <CheckIcon />
                          </IconButton>
                          {deleting && (
                            <CircularProgress
                              size={24}
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                marginTop: -12,
                                marginLeft: -12,
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    ) : (
                      <FormButtons
                        onConfirmClick={() =>
                          setFurnisherToEdit({ ...furnisher } as FurnisherDto)
                        }
                        onDeleteClick={() => setCheckDelete(true)}
                        disabled={false}
                        cancelButtonVisible={false}
                        deleteButtonVisible={true}
                        confirmIcon="EditContact"
                        confirmText={t("common.edit")}
                      />
                    )}
                  </CardActions>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={t("furnishers.statistics")} />
                  <CardContent>
                    <Grid container spacing={1}>
                      <Grid item sm={6} xs={12}>
                        <FluentUiDatePicker
                          required
                          label={t("services.startDate")}
                          placeholder={t("services.startDate")}
                          value={startDate}
                          onSelectDate={(date: Date | null | undefined): void => {
                            if (date) setStartDate(date);
                          }}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <FluentUiDatePicker
                          required
                          label={t("services.endDate")}
                          placeholder={t("services.endDate")}
                          value={endDate}
                          onSelectDate={(date: Date | null | undefined): void => {
                            if (date) setEndDate(date);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        {!statisticLoading ? (
                          statistic != null && (statistic.count ?? 0) > 0 ? (
                            <Grid container spacing={1}>
                              <Grid item sm={6} xs={12}>
                                <LabelFieldValue
                                  label={t("furnishers.expenseCount")}
                                  value={statistic.count}
                                  variant="xLarge"
                                />
                              </Grid>
                              <Grid item sm={6} xs={12}>
                                <LabelFieldValue
                                  label={t("furnishers.totalExpenses")}
                                  value={numberToMad(statistic.totalAmount)}
                                  variant="xLarge"
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            <NoResult />
                          )
                        ) : (
                          <Loading />
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={t("furnishers.expensesTitle")} />
                  <CardContent>
                    {!expensesLoading ? (
                      expensesResult != null &&
                      expensesResult.data != null &&
                      expensesResult.data.length > 0 ? (
                        <>
                          <List>
                            {expensesResult.data.map((e: ExpenseDto) => (
                              <div key={e.expenseId}>
                                <ListItem
                                  alignItems="flex-start"
                                  style={{
                                    paddingTop: "0px",
                                    paddingBottom: "0px",
                                  }}
                                >
                                  <ListItemAvatar>
                                    <Avatar>
                                      <MoneyOffIcon />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={numberToMad(e.amount ?? 0)}
                                    secondary={
                                      <React.Fragment>
                                        {dateToString(e.date)}
                                        {e.comment ? ", " + e.comment : ""}
                                      </React.Fragment>
                                    }
                                  />
                                </ListItem>
                                <Divider />
                              </div>
                            ))}
                          </List>
                          <TablePagination
                            component="div"
                            count={expensesResult.totalElements ?? 0}
                            page={expensesPageIndex}
                            rowsPerPage={expensesPageSize}
                            rowsPerPageOptions={[expensesPageSize]}
                            onPageChange={(event, newPage) => {
                              setExpensesPageIndex(newPage);
                            }}
                          />
                        </>
                      ) : (
                        <NoResult />
                      )
                    ) : (
                      <Loading />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : (
          <NoResult />
        )
      ) : (
        <Loading />
      )}
      <Panel
        isBlocking={true}
        headerText={t("furnishers.editTitle")}
        isOpen={isAdmin && furnisherToEdit != null}
        onDismiss={() => {
          setFurnisherToEdit(null);
        }}
        closeButtonAriaLabel={t("common.close")}
      >
        {furnisherToEdit != null && (
          <CRUDFurnisher
            furnisher={furnisherToEdit}
            furnisherChanged={() => {
              setFurnisher(null);
              setFurnisherToEdit(null);
            }}
          />
        )}
      </Panel>
    </Container>
  );
}
